import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { PricingModelItem } from "../../../model/pricingmodel";
import { ReactElement, ReactNode, useRef, useState } from "react";
import {
  generateIdempotencyKey,
  postPricingModelItemPurchaseIntentAsync,
} from "../../../services/pricingmodels";
import "./AddNewPaymentMethod.css";
import { HStack, VStack } from "../../Stack";
import classnames from "classnames";
import TalaeraSpinner from "../../TalaeraSpinner/TalaeraSpinner";
import PaymentSucceedModal from "./PaymentSucceedModal";
import PaymentFailedModal from "./PaymentFailedModal";
import Submit from "../Submit";

interface LabeledInputProps {
  children: ReactElement | ReactElement[];
  label: string;
  className?: string | null;
}

function LegendaryContainer({ label, children, className }: LabeledInputProps) {
  /*
  A container that adds a legend (small text) to the top of the border.
  */
  return (
    <div className={classnames("labeled-input-container", className)}>
      <div className="label">{label}</div>
      {children}
    </div>
  );
}

interface StripeLegendaryContainerProps {
  label: string;
  className?: string;
  renderComponent: (onFocus: () => void, onBlur: () => void) => JSX.Element;
}

function StripeLegendaryContainer({
  label,
  renderComponent,
  className,
}: StripeLegendaryContainerProps) {
  /*
  Since Stripe components are technically iFrames there's very little could be done to modify their appearance, thus need
  for such workarounds.
   */
  const [focused, setFocused] = useState<string | null>();
  const component = renderComponent(
    () => setFocused("focused"),
    () => setFocused(null)
  );
  return (
    <LegendaryContainer label={label} className={classnames(className, focused)}>
      {component}
    </LegendaryContainer>
  );
}

interface AddPaymentOptionProps {
  plan: PricingModelItem;
  onSuccess: () => void;
  onDismiss: () => void;
  className?: string;
  hideBackOptions?: boolean;
  reload: () => void;
  secondaryPaymentAction?: ReactNode;
}

export default function AddNewPaymentMethod({
  plan,
  onSuccess,
  className,
  reload,
  secondaryPaymentAction,
}: AddPaymentOptionProps) {
  const stripe = useStripe();
  const elements = useElements();
  const inProgress = useRef(false);
  const [postal, setPostal] = useState();
  const [loading, setLoading] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(generateIdempotencyKey());
  const [showModalDialog, setShowModalDialog] = useState<
    "success" | "failed" | "failed_reload_on_close" | null
  >(null);

  const updateIdempotencyKey = () => {
    setIdempotencyKey(generateIdempotencyKey());
  };

  const stripeOptions = {
    disabled: loading,
    classes: {
      base: "stripe-element",
    },
    style: {
      base: {
        fontSize: "16px",
        fontFamily: "serif",
        fontWeight: "normal",
        fontStyle: "normal",
        fontStretch: "normal",
        lineHeight: "normal",
        letterSpacing: "normal",
        color: "#000000",
      },
    },
  };

  const submitPayment = async () => {
    // Check for double clicks.
    if (inProgress.current) {
      return;
    }
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    const cardNumber = elements.getElement(CardNumberElement);
    if (cardNumber === null) {
      return;
    }

    const expiry = elements.getElement(CardCvcElement);
    if (expiry === null) {
      return;
    }

    const cvc = elements.getElement(CardCvcElement);
    if (cvc === null) {
      return;
    }

    if (!postal) {
      return;
    }
    inProgress.current = true;
    setLoading(true);

    let client_secret, status;
    try {
      const response = await postPricingModelItemPurchaseIntentAsync({
        id: plan.id!,
        idempotencyKey: idempotencyKey,
        save_pm: true,
      });
      client_secret = response.client_secret;
      status = response.status;
    } catch (e) {
      setLoading(false);
      inProgress.current = false;
      const error = e as any;
      if (error?.statusCode === 400) {
        // This idempotency key was already used and failed. There's a chance that previous attempt succeeded though,
        // have to reload.
        updateIdempotencyKey();
        setShowModalDialog("failed_reload_on_close");
        return;
      } else {
        // Payment was unsuccessful.
        if (error?.statusCode === 402) {
          // This idempotency key has failed.
          updateIdempotencyKey();
        }
        setShowModalDialog("failed");
      }
      return;
    }

    if (status !== "pending" && status !== "processing") {
      setLoading(false);
      inProgress.current = false;
      setShowModalDialog("failed");
      return;
    }

    try {
      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardNumber,
          billing_details: {
            address: {
              postal_code: postal,
            },
          },
        },
      });
      if (result.error) {
        updateIdempotencyKey();
        setShowModalDialog("failed");
        return;
      }
      setShowModalDialog("success");
    } catch (e) {
      setShowModalDialog("failed");
    } finally {
      setLoading(false);
      inProgress.current = false;
    }
  };

  const hdPostcode = (evt: any) => {
    let value = evt.target.value;
    if (value === undefined) {
      return;
    }
    setPostal(value);
  };

  const onFormSubmit = (evt: any) => {
    evt?.preventDefault && evt.preventDefault();
    submitPayment();
  };

  return (
    <form className={classnames(className)} onSubmit={onFormSubmit}>
      {showModalDialog === "success" && (
        <PaymentSucceedModal plan={plan} onClose={onSuccess} />
      )}
      {showModalDialog === "failed" && (
        <PaymentFailedModal onClose={() => setShowModalDialog(null)} />
      )}
      {showModalDialog === "failed_reload_on_close" && (
        <PaymentFailedModal
          onClose={() => {
            setShowModalDialog(null);
            reload();
          }}
        />
      )}
      <VStack gap={16} align="stretch" justify="flex-start">
        <StripeLegendaryContainer
          className="card-number"
          label="Card Number"
          renderComponent={(onFocus, onBlur) => (
            <CardNumberElement
              options={stripeOptions}
              onBlur={onBlur}
              onFocus={onFocus}
            />
          )}
        />
        <HStack justify="stretch" gap={16} className="supplemental-container">
          <StripeLegendaryContainer
            label={"Expiration Date"}
            renderComponent={(onFocus, onBlur) => (
              <CardExpiryElement
                options={stripeOptions}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            )}
          />
          <StripeLegendaryContainer
            label="CVV/CVC"
            renderComponent={(onFocus, onBlur) => (
              <CardCvcElement options={stripeOptions} onFocus={onFocus} onBlur={onBlur} />
            )}
          />
          <LegendaryContainer label="Postcode/ZIP" className={"post-code"}>
            <input
              placeholder="Postcode/ZIP"
              maxLength={9}
              type="text"
              id="postcode"
              disabled={loading}
              onChange={hdPostcode}
              value={postal}
            />
          </LegendaryContainer>
        </HStack>
        <Submit pricingItem={plan!} hdSubmit={onFormSubmit}>
          {secondaryPaymentAction}
        </Submit>
      </VStack>
      {loading && <TalaeraSpinner />}
    </form>
  );
}
